import { useMemo } from 'react';
import { CircleDot, Calendar, Tag, UserCog } from 'lucide-react';
import {
  FilterBar,
  type FilterDefinition,
  type FilterFieldType,
  type FilterState,
} from '@/components/filter-bar/filter-bar';
import { useUsers } from '@/hooks/use-users';
import { useTeams } from '@/hooks/use-teams';
import { AssigneePickerList, type AssigneeValue } from '@/components/custom/assignee-picker';
import { useLabelOptions } from '@/lib/select-label-options';

// `show` (My / All / Unassigned) is no longer in this dropdown — the
// scope tabs in `TaskListHeader` are the permanent UI for that filter.
const STATIC_DEFINITIONS: FilterDefinition[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'multi-select',
    icon: CircleDot,
    options: [
      { value: 'open', label: 'Open' },
      { value: 'done', label: 'Done' },
    ],
  },
  {
    key: 'date',
    label: 'Date',
    type: 'date-range' as FilterFieldType,
    icon: Calendar,
  },
];

export function useTaskFilterDefinitions(): FilterDefinition[] {
  // No category filter — the task inbox surfaces every label
  // (document_type + workflow_tag + procedural) so users can pick
  // any of them as a task filter.
  const labelOptions = useLabelOptions();
  // Assignee options come from two sources — users and teams. Encoded as
  // `${type}:${id}` strings so the generic FilterBar primitive stays
  // unchanged. The URL serializer (in `task-filter-utils.ts`) splits on
  // the colon to write `assignedTo` + `assigneeType` URL params.
  const { data: usersData } = useUsers();
  const { data: teamsData } = useTeams();

  // Assignee options drive the chip's display label (so it reads
  // "Assignee is Cathy" not "Assignee is user:uuid"). Built once per
  // users/teams change — must be a top-level hook, not nested inside
  // the outer useMemo.
  // Deps pin to `.items` / the teams array directly rather than the
  // wrapping query objects. A TanStack Query refetch produces a new
  // wrapper reference even when data is byte-identical; pinning to the
  // inner reference avoids rebuilding (and re-rendering downstream chip
  // + popover) on no-op refetches.
  const assigneeOptions = useMemo(() => {
    const out: { value: string; label: string }[] = [];
    for (const u of usersData?.items ?? []) {
      if (u.rowStatus !== 0) continue;
      const name = `${u.firstName} ${u.lastName}`.trim() || u.email;
      out.push({ value: `user:${u.id}`, label: name });
    }
    for (const t of teamsData ?? []) {
      out.push({ value: `team:${t.id}`, label: `${t.name} (Team)` });
    }
    return out;
  }, [usersData?.items, teamsData]);

  return useMemo(() => {
    const labelsDef: FilterDefinition = {
      key: 'labels',
      label: 'Labels',
      type: 'multi-select',
      icon: Tag,
      options: labelOptions,
    };

    // Assignee — same UI as the AssigneePicker used elsewhere in tasks
    // (avatars, group headings, role names, search). Wired through
    // FilterBar's `customEditor` slot so the chip/popover/state-machine
    // stays generic while the picker rendering is shared with the
    // direct-assignment surface. The polymorphic value is encoded as
    // `user:<uuid>` / `team:<uuid>` so the URL serializer can collapse
    // to a single (assignedTo, assigneeType) pair.
    const assigneeDef: FilterDefinition = {
      key: 'assignee',
      label: 'Assignee',
      type: 'multi-select',
      icon: UserCog,
      // Options drive the FilterChip's display text — when a value is
      // selected, the chip shows the matching `label` so the chip
      // reads "Assignee is Cathy" instead of "Assignee is user:uuid".
      options: assigneeOptions,
      // Match the AssigneePicker's natural width so the popover doesn't
      // squeeze the rows.
      popoverWidthClass: 'w-[280px]',
      customEditor: ({ selected, onToggle, onClear, onClose }) => {
        // Multi-select shape, single-select semantics — first value wins.
        const first = selected[0];
        const current: AssigneeValue | null = first ? decodeAssignee(first, assigneeOptions) : null;
        return (
          <AssigneePickerList
            selected={current}
            allowedTypes={['user', 'team']}
            allowUnassign
            onSelect={(next) => {
              // Switch selection: clear the previous value (if any),
              // then set the new one. FilterBar's onToggle flows into
              // the same state machine the default editor uses.
              if (first) onToggle(first, false);
              onToggle(`${next.type}:${next.id}`, true);
              onClose();
            }}
            onUnassign={() => {
              onClear();
              onClose();
            }}
          />
        );
      },
    };
    return [...STATIC_DEFINITIONS, assigneeDef, labelsDef];
  }, [labelOptions, assigneeOptions]);
}

/**
 * Decode the encoded `<type>:<id>` string into an AssigneeValue. Looks
 * up the human-readable name from the options list (which the parent
 * just built from useUsers + useTeams) so the picker can render the
 * selected row's avatar / name correctly.
 */
function decodeAssignee(
  encoded: string,
  options: { value: string; label: string }[]
): AssigneeValue | null {
  const idx = encoded.indexOf(':');
  if (idx < 0) return null;
  const type = encoded.slice(0, idx);
  const id = encoded.slice(idx + 1);
  if (type !== 'user' && type !== 'team') return null;
  const opt = options.find((o) => o.value === encoded);
  // Strip the " (Team)" suffix we added for team labels — the picker
  // already shows the Users-icon for team rows, so the duplicate
  // suffix would read like "All Staff (Team) (Team)".
  const name = (opt?.label ?? id).replace(/ \(Team\)$/, '');
  return { type, id, name };
}

interface TaskFilterBarProps {
  filterState: FilterState;
  onFilterChange: (state: FilterState) => void;
  definitions: FilterDefinition[];
}

export function TaskFilterBar({
  filterState,
  onFilterChange,
  definitions,
}: TaskFilterBarProps): React.ReactElement {
  return (
    <FilterBar
      definitions={definitions}
      value={filterState}
      onChange={onFilterChange}
      className=""
    />
  );
}
